import { type CheckCaseActionReadinessSettings } from '@/agent/types/case-action'
import HttpService from '@/common/services/HttpService'
import {
  CaseActionModalEnum,
  type CaseActionDTOV1,
  type CompleteActionDTOV1,
  type ReopenActionDTOV1,
  type AskForValidationActionDTOV1,
  type CancelCaseActionDTOV1,
  type UndoValidationCaseActionDTOV1,
  type ApproveCaseActionDTOV1,
  type DisapproveCaseActionDTOV1,
  type ReleaseCaseActionDTOV1,
  type AssignCaseActionDTOV1,
  type ReassignCaseActionDTOV1,
  type TransferCaseActionDTOV1,
  type StackCaseActionDTOV1,
  type StackAndReleaseCaseActionDTOV1,
  type StackAndReassignCaseActionDTOV1,
  type StackAndTransferCaseActionDTOV1,
  type UpdateDateDueCaseActionDTOV1,
  type PerformCaseActionPayloadDTOV1,
  type CaseActionResponse,
  type ListCaseActionsParams,
  type CaseActionsMetadataDTOV1,
  type ListCaseActionDTOV1,
} from '@/common/types/caseAction'
import { type ApiResponseData } from '@/common/types/http'
import { PrefixTranslationsEnum } from '@/common/types/i18n'

export class CaseActionReadinessError extends Error {
  errors: ApiResponseData[]

  constructor(errors: ApiResponseData[]) {
    super('Case action readiness check failed')

    this.name = 'CaseActionReadinessError'
    this.errors = errors
  }
}

export const checkCaseActionReadiness = async ({ action, caseId }: CheckCaseActionReadinessSettings) => {
  const response = await HttpService.getData<ApiResponseData[]>(`/api/v1/cases/${caseId}/action-readiness`, {
    params: {
      action: action,
    },
  })

  if (response.length) {
    throw new CaseActionReadinessError(response)
  }
}

export const performCompleteCaseAction = (caseId: number, data: CompleteActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/complete`, data)
}

export const performCompleteOnValidationCaseAction = (caseId: number, data: CompleteActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/complete-on-validation`, data)
}

export const performReopenCaseAction = (caseId: number, data: ReopenActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/reopen`, data)
}

export const performAskForValidationCaseAction = (caseId: number, data: AskForValidationActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/ask-for-validation`, data)
}

export const performCancelCaseAction = (caseId: number, data: CancelCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/cancel`, data)
}

export const performUndoValidationCaseAction = (caseId: number, data: UndoValidationCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/cancel-validation-request`, data)
}

export const performApproveAction = (caseId: number, data: ApproveCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/approve`, data)
}

export const performDisapproveAction = (caseId: number, data: DisapproveCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/disapprove`, data)
}

export const performReleaseAction = (caseId: number, data: ReleaseCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/release`, data)
}

export const performAssignCaseAction = (caseId: number, data: AssignCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/assign`, data)
}

export const performReassignCaseAction = (caseId: number, data: ReassignCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/reassign`, data)
}

export const performTransferCaseAction = (caseId: number, data: TransferCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/transfer`, data)
}

export const performStackCaseAction = (caseId: number, data: StackCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/put-on-hold`, data)
}

export const performStackAndReleaseCaseAction = (caseId: number, data: StackAndReleaseCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1[]>(`/api/v1/cases/${caseId}/put-on-hold-and-release`, data)
}

export const performStackAndReassignCaseAction = (caseId: number, data: StackAndReassignCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1[]>(`/api/v1/cases/${caseId}/put-on-hold-and-reassign`, data)
}

export const performStackAndTransferCaseAction = (caseId: number, data: StackAndTransferCaseActionDTOV1) => {
  return HttpService.putData<CaseActionDTOV1[]>(`/api/v1/cases/${caseId}/put-on-hold-and-transfer`, data)
}

export const performUpdateDateDueCaseAction = (caseId: number, data: UpdateDateDueCaseActionDTOV1) => {
  return HttpService.patchData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/due-date`, data)
}

export const performTakeOnCaseAction = (caseId: number) => {
  return HttpService.putData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/take-on`)
}

export const performCommentCaseAction = (caseId: number, comment: string) => {
  return HttpService.postData<CaseActionDTOV1>(`/api/v1/cases/${caseId}/comment`, comment, {
    headers: {
      'Content-Type': 'text/plain',
    },
  })
}

export const getWakeupDate = (scheduleCode: string, reasonCode: string) => {
  return HttpService.getData<string>('/api/v1/wakeupdate-calculation', {
    params: {
      scheduleName: `${PrefixTranslationsEnum.SCHEDULE}${scheduleCode}`,
      reasonCode,
    },
  })
}

/**
 * @param ids Case action ids
 * @returns array of CaseActionDTOV1
 */
export const fetchCaseActions = async (ids: number[]): Promise<CaseActionDTOV1[]> => {
  return HttpService.postData<CaseActionDTOV1[]>('/api/v1/bulk-cases-actions/fetch', { ids })
}

export const CaseActionsMap = new Map<CaseActionModalEnum, (caseId: number, data: PerformCaseActionPayloadDTOV1) => Promise<CaseActionResponse>>([
  [CaseActionModalEnum.COMPLETE, performCompleteCaseAction],
  [CaseActionModalEnum.COMPLETEONVALIDATION, performCompleteOnValidationCaseAction],
  [CaseActionModalEnum.REOPEN, performReopenCaseAction],
  [CaseActionModalEnum.ASKFORVALIDATION, performAskForValidationCaseAction],
  [CaseActionModalEnum.DELETE, performCancelCaseAction],
  [CaseActionModalEnum.UNDOVALIDATION, performUndoValidationCaseAction],
  [CaseActionModalEnum.APPROVE, performApproveAction],
  [CaseActionModalEnum.DISAPPROVE, performDisapproveAction],
  [CaseActionModalEnum.PUTBACKINPOOL, performReleaseAction],
  [CaseActionModalEnum.ASSIGN, performAssignCaseAction],
  [CaseActionModalEnum.REASSIGN, performReassignCaseAction],
  [CaseActionModalEnum.TRANSFER, performTransferCaseAction],
  [CaseActionModalEnum.STACK, performStackCaseAction],
  [CaseActionModalEnum.STACKANDPUTBACKINPOOL, performStackAndReleaseCaseAction],
  [CaseActionModalEnum.STACKANDREASSIGN, performStackAndReassignCaseAction],
  [CaseActionModalEnum.STACKANDTRANSFER, performStackAndTransferCaseAction],
  [CaseActionModalEnum.UPDATEDUEDATE, performUpdateDateDueCaseAction],
])

export const listCaseActions = async (caseId: number, query?: ListCaseActionsParams) => {
  const params = query
    ? {
        ...query,
        user: query.user?.length ? query.user.join() : undefined,
        type: query.type?.length ? query.type.join() : undefined,
      }
    : undefined

  const res = await HttpService.get<ListCaseActionDTOV1[]>(`/api/v1/cases/${caseId}/actions`, {
    params,
  })

  return {
    caseActions: res.data,
    reachedEnd: res.status !== 206,
  }
}

/**
 * @param caseId Case action id
 * @returns array of CaseActionsMetadataDTOV1
 */
export const fetchCaseActionsMetadata = (caseId: number) => {
  return HttpService.getData<CaseActionsMetadataDTOV1>(`/api/v1/cases/${caseId}/actions-metadata`)
}
